import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface UseRecordingNavigationGuardProps {
  isRecording: boolean;
  onEndRecording: () => Promise<void> | void;
  onDiscardRecording: () => Promise<void> | void;
  recordingDuration?: string;
}

export const useRecordingNavigationGuard = ({
  isRecording,
  onEndRecording,
  onDiscardRecording,
  recordingDuration
}: UseRecordingNavigationGuardProps) => {
  const [showGuard, setShowGuard] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Store original navigation function
  const originalNavigate = useCallback((path: string) => {
    if (isRecording && !isProcessing) {
      // Block navigation and show guard
      setPendingNavigation(path);
      setShowGuard(true);
      return false; // Indicate navigation was blocked
    } else {
      // Allow navigation
      navigate(path);
      return true; // Indicate navigation proceeded
    }
  }, [isRecording, isProcessing, navigate]);

  // Handle continue recording (dismiss guard)
  const handleContinueRecording = useCallback(() => {
    setShowGuard(false);
    setPendingNavigation(null);
  }, []);

  // Handle end and save
  const handleEndAndSave = useCallback(async () => {
    if (!pendingNavigation) return;

    setIsProcessing(true);
    try {
      await onEndRecording();
      // After successful save, navigate to pending destination
      navigate(pendingNavigation);
      setShowGuard(false);
      setPendingNavigation(null);
    } catch (error) {
      console.error('Error ending recording:', error);
      // Could show error message here
    } finally {
      setIsProcessing(false);
    }
  }, [pendingNavigation, onEndRecording, navigate]);

  // Handle discard
  const handleDiscard = useCallback(async () => {
    if (!pendingNavigation) return;

    setIsProcessing(true);
    try {
      await onDiscardRecording();
      // After discard, navigate to pending destination
      navigate(pendingNavigation);
      setShowGuard(false);
      setPendingNavigation(null);
    } catch (error) {
      console.error('Error discarding recording:', error);
      // Could show error message here
    } finally {
      setIsProcessing(false);
    }
  }, [pendingNavigation, onDiscardRecording, navigate]);

  // Browser beforeunload protection
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isRecording) {
        event.preventDefault();
        event.returnValue = 'You have an active recording. Are you sure you want to leave?';
        return event.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isRecording]);

  // Reset guard when recording stops naturally
  useEffect(() => {
    if (!isRecording && showGuard) {
      setShowGuard(false);
      setPendingNavigation(null);
    }
  }, [isRecording, showGuard]);

  return {
    showGuard,
    isProcessing,
    recordingDuration,
    handleContinueRecording,
    handleEndAndSave,
    handleDiscard,
    guardedNavigate: originalNavigate // Use this instead of navigate
  };
};
